import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Result, Button, Spin, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import { verifyEmailToken } from '../services/authService';

const { Title, Paragraph } = Typography;

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { themeConfig } = useTheme();
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(true);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid verification link. No token provided.');
      setShowConfirmation(false);
    }
  }, [searchParams]);

  const verifyEmail = async () => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid verification link. No token provided.');
      return;
    }

    setLoading(true);
    setShowConfirmation(false);

    try {
      const data = await verifyEmailToken(token);
      setVerified(true);

      if (data.already_verified) {
        console.log('[EmailVerification] Email was already verified');
      }
    } catch (err) {
      setError(err.message || 'Email verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: themeConfig.sidebarGradient,
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          borderRadius: 16,
          border: `1px solid ${themeConfig.border}`,
          background: themeConfig.background,
        }}
        bordered={false}
      >
        {showConfirmation ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <MailOutlined style={{ fontSize: 72, color: themeConfig.primary, marginBottom: 24 }} />
            <Title level={2} style={{ color: themeConfig.textPrimary, marginBottom: 16 }}>
              Verify Your Email Address
            </Title>
            <Paragraph style={{ color: themeConfig.textSecondary, fontSize: 16, marginBottom: 32 }}>
              Click the button below to confirm your email address and activate your account.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={verifyEmail}
              loading={loading}
              style={{
                background: themeConfig.primary,
                borderColor: themeConfig.primary,
                height: 56,
                fontSize: 18,
                fontWeight: 600,
                paddingLeft: 48,
                paddingRight: 48,
              }}
            >
              Verify My Email
            </Button>
            <Paragraph style={{ color: themeConfig.textSecondary, fontSize: 14, marginTop: 24 }}>
              This link will expire in 48 hours.
            </Paragraph>
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: themeConfig.primary }} spin />} />
            <Title level={3} style={{ marginTop: 24, color: themeConfig.textPrimary }}>
              Verifying Your Email...
            </Title>
            <Paragraph style={{ color: themeConfig.textSecondary }}>
              Please wait while we verify your email address.
            </Paragraph>
          </div>
        ) : verified ? (
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={<span style={{ color: themeConfig.textPrimary }}>Email Verified Successfully!</span>}
            subTitle={
              <span style={{ color: themeConfig.textSecondary }}>
                Your email has been verified. You can now log in.
              </span>
            }
            extra={[
              <Button
                type="primary"
                size="large"
                onClick={handleGoToLogin}
                key="login"
                style={{
                  background: themeConfig.primary,
                  borderColor: themeConfig.primary,
                  height: 48,
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                Go to Login
              </Button>,
            ]}
          />
        ) : (
          <Result
            icon={<CloseCircleOutlined style={{ color: '#f5222d' }} />}
            title={<span style={{ color: themeConfig.textPrimary }}>Verification Failed</span>}
            subTitle={<span style={{ color: themeConfig.textSecondary }}>{error}</span>}
            extra={[
              <Button
                type="primary"
                size="large"
                onClick={handleGoToLogin}
                key="login"
                style={{
                  background: themeConfig.primary,
                  borderColor: themeConfig.primary,
                  height: 48,
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                Back to Login
              </Button>,
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default EmailVerification;
